import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi, type AppVersions, type ReleaseNote } from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { InfoBubble } from '../components/Screen.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import {
  EMPTY_FORM,
  NOTES_LIMIT,
  bodyFromForm,
  deliveryLine,
  formFromNote,
  formProblems,
  isDirty,
  localDate,
  missingForPublish,
  nextVersions,
  previewBlocks,
  publishQuestion,
  requiredQuestion,
  type ReleaseForm,
} from '../lib/release-notes.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  Field,
  HoldButton,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
  inputStyle,
} from '../components/ui.js';

/**
 * What each released version changed, written for the people who use the app
 * (RidgeWill/keyodo#709). The app shows published notes as "What's new".
 *
 * One note per version that reaches people. A version is MAJOR.MINOR.PATCH:
 * major when the app changes as a whole, minor for something new people can
 * use, patch for fixes only. The store's build number gets no note.
 *
 * A note is a draft until published. A published note can be fixed in place,
 * but not deleted. An agent can draft one; a person publishes it.
 *
 * "Required" marks a release older apps must not run below. The highest
 * published release is the app's latest version and the highest published
 * Required one its minimum (RidgeWill/auth#680); auth sends both to the app on
 * every change. So publishing means the build is live in both stores, and both
 * Publish and turning Required on ask first.
 *
 * "Significant" marks a release that changes what data is collected, the age
 * rating, how the app makes money, or a major feature (keyodo#673). The stores
 * and guardians must be told, and the note for parents is what they are told.
 * It is never shown in the app.
 */
export function ApplicationReleases({ applicationId, appName }: { applicationId: string; appName: string }) {
  const { state, reload } = useAsync(() => adminApi.releaseNotes(applicationId), [applicationId]);
  const [editing, setEditing] = useState<{ note: ReleaseNote | null } | null>(null);
  const [confirming, setConfirming] = useState<{ note: ReleaseNote; kind: 'publish' | 'required' } | null>(null);
  const action = useAction();
  const mayWrite = useCan('application:write');

  return (
    <AsyncBoundary state={state}>
      {({ notes, versions }) => (
        <Panel
          title="Releases"
          info={
            <InfoBubble label="About releases">
              One note for each version of {appName} that reaches people, shown in the app as &ldquo;What&rsquo;s
              new&rdquo; once published. Write it for the people using the app: what changed for them, with no
              ticket numbers. A published note can be fixed here at any time. Each environment keeps its own notes.
              <br />
              <br />
              The latest version is the highest published release, and the minimum version is the highest published
              release marked Required. You can edit both, but the next publish, unpublish, delete or Required change
              works them out again and replaces your edit.
            </InfoBubble>
          }
          action={
            <Button
              disabled={action.running || !mayWrite}
              title={mayWrite ? undefined : whyDisabled('writing release notes')}
              onClick={() => setEditing({ note: null })}
            >
              + New release note
            </Button>
          }
        >
          {action.outcome ? (
            <PanelBody>
              <OutcomeNote outcome={action.outcome} />
            </PanelBody>
          ) : null}

          <VersionsSection
            applicationId={applicationId}
            versions={versions}
            mayWrite={mayWrite}
            onChanged={() => reload()}
          />

          {notes.length === 0 ? (
            <EmptyState
              title="No release notes yet"
              suggestion="Add one for the version people are running now, then one for each release after it."
            />
          ) : (
            <Table columns={['Version', 'Released', 'Status', 'Last edited', '']} actionsLast>
              {notes.map((n) => {
                const missing = missingForPublish(n);
                return (
                  <tr key={n.id}>
                    <Td>
                      <strong>{n.version}</strong>
                    </Td>
                    <Td>
                      <span style={{ whiteSpace: 'nowrap', ...(n.releasedOn ? {} : { color: C.textMid }) }}>
                        {n.releasedOn ?? 'Not dated'}
                      </span>
                    </Td>
                    <Td>
                      <span style={{ display: 'inline-flex', gap: '.3rem' }}>
                        {n.published ? <Badge tone="good">Published</Badge> : <Badge>Draft</Badge>}
                        {n.required ? <Badge tone="danger">Required</Badge> : null}
                        {n.significant ? <Badge tone="warn">Significant</Badge> : null}
                      </span>
                    </Td>
                    <Td>
                      <span style={{ whiteSpace: 'nowrap' }}>
                        <When iso={n.updatedAt} />
                        {n.updatedByName ? <span style={{ color: C.textMid }}> by {n.updatedByName}</span> : null}
                      </span>
                    </Td>
                    <Td action>
                      <span style={{ display: 'inline-flex', gap: '.35rem' }}>
                        <Button disabled={action.running || !mayWrite} onClick={() => setEditing({ note: n })}>
                          Edit
                        </Button>
                        {n.required ? (
                          <Button
                            disabled={action.running || !mayWrite}
                            title="Stop requiring this version. Older apps are no longer blocked by it."
                            onClick={() =>
                              void action
                                .run(async () => {
                                  await adminApi.setReleaseRequired(applicationId, n.id, false);
                                  return `${n.version} is no longer required.`;
                                })
                                .then(() => reload())
                            }
                          >
                            Not required
                          </Button>
                        ) : (
                          <Button
                            disabled={action.running || !mayWrite}
                            title="Block every older version until it updates."
                            onClick={() => setConfirming({ note: n, kind: 'required' })}
                          >
                            Make required
                          </Button>
                        )}
                        {n.published ? (
                          <HoldButton
                            disabled={action.running || !mayWrite}
                            title="Hold to unpublish. It goes back to a draft, and the app's versions are worked out without it."
                            onHold={() =>
                              void action
                                .run(async () => {
                                  await adminApi.unpublishReleaseNote(applicationId, n.id);
                                  return `Unpublished ${n.version}. It is a draft again.`;
                                })
                                .then(() => reload())
                            }
                          >
                            Unpublish
                          </HoldButton>
                        ) : (
                          <>
                            <Button
                              disabled={action.running || !mayWrite || missing.length > 0}
                              title={missing.length > 0 ? `Add ${missing.join(' and ')} first.` : undefined}
                              onClick={() => setConfirming({ note: n, kind: 'publish' })}
                            >
                              Publish
                            </Button>
                            <HoldButton
                              disabled={action.running || !mayWrite}
                              title="Hold to delete this draft."
                              onHold={() =>
                                void action
                                  .run(async () => {
                                    await adminApi.deleteReleaseNote(applicationId, n.id);
                                    return `Deleted the draft for ${n.version}.`;
                                  })
                                  .then(() => reload())
                              }
                            >
                              Delete
                            </HoldButton>
                          </>
                        )}
                      </span>
                    </Td>
                  </tr>
                );
              })}
            </Table>
          )}

          {confirming ? (
            <ConfirmModal
              title={confirming.kind === 'publish' ? `Publish ${confirming.note.version}?` : `Make ${confirming.note.version} required?`}
              question={confirming.kind === 'publish' ? publishQuestion(confirming.note) : requiredQuestion(confirming.note)}
              confirmLabel={confirming.kind === 'publish' ? `Yes, publish ${confirming.note.version}` : 'Yes, make it required'}
              onClose={() => setConfirming(null)}
              onConfirm={async () => {
                const { note, kind } = confirming;
                setConfirming(null);
                await action.run(async () => {
                  if (kind === 'publish') {
                    await adminApi.publishReleaseNote(applicationId, note.id, localDate(new Date()));
                    return `Published ${note.version}.`;
                  }
                  await adminApi.setReleaseRequired(applicationId, note.id, true);
                  return `${note.version} is required.`;
                });
                reload();
              }}
            />
          ) : null}

          {editing ? (
            <ReleaseModal
              note={editing.note}
              suggestions={editing.note ? null : nextVersions(notes)}
              onClose={() => setEditing(null)}
              onSave={async (form) => {
                if (editing.note) await adminApi.updateReleaseNote(applicationId, editing.note.id, bodyFromForm(form));
                else await adminApi.createReleaseNote(applicationId, bodyFromForm(form));
                setEditing(null);
                reload();
              }}
            />
          ) : null}
        </Panel>
      )}
    </AsyncBoundary>
  );
}

/**
 * The app's latest and minimum versions, the note that a release change
 * replaces a manual edit, and Sync with how the last delivery went.
 */
function VersionsSection({
  applicationId,
  versions,
  mayWrite,
  onChanged,
}: {
  applicationId: string;
  versions: AppVersions;
  mayWrite: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [latest, setLatest] = useState(versions.latestVersion ?? '');
  const [minimum, setMinimum] = useState(versions.minimumVersion ?? '');
  const action = useAction();
  const line = deliveryLine(versions);
  const tone = { good: C.successText, warn: C.warnText, danger: C.dangerText, neutral: C.textMid }[line.tone];

  return (
    <PanelBody>
      <div style={{ display: 'grid', gap: '.6rem' }}>
        <OutcomeNote outcome={action.outcome} />
        {editing ? (
          <div style={{ display: 'flex', gap: '.8rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Field label="Latest version" hint="Older apps are prompted to update.">
              <input value={latest} onChange={(e) => setLatest(e.target.value)} placeholder="None" spellCheck={false} style={inputStyle} />
            </Field>
            <Field label="Minimum version" hint="Older apps are blocked until they update.">
              <input value={minimum} onChange={(e) => setMinimum(e.target.value)} placeholder="None" spellCheck={false} style={inputStyle} />
            </Field>
            <span style={{ display: 'inline-flex', gap: '.35rem', paddingBottom: '.2rem' }}>
              <Button onClick={() => setEditing(false)}>Cancel</Button>
              <Button
                disabled={action.running}
                onClick={() =>
                  void action
                    .run(async () => {
                      await adminApi.setAppVersions(applicationId, {
                        latestVersion: latest.trim() || null,
                        minimumVersion: minimum.trim() || null,
                      });
                      setEditing(false);
                      return 'Saved the versions and sent them to the app.';
                    })
                    .then(() => onChanged())
                }
              >
                {action.running ? 'Saving…' : 'Save'}
              </Button>
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '1.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span>
              <span style={{ color: C.textMid }}>Latest version </span>
              <strong>{versions.latestVersion ?? 'None'}</strong>
            </span>
            <span>
              <span style={{ color: C.textMid }}>Minimum version </span>
              <strong>{versions.minimumVersion ?? 'None'}</strong>
            </span>
            <span style={{ display: 'inline-flex', gap: '.35rem' }}>
              <Button
                disabled={action.running || !mayWrite}
                title={mayWrite ? undefined : whyDisabled('changing the versions')}
                onClick={() => {
                  setLatest(versions.latestVersion ?? '');
                  setMinimum(versions.minimumVersion ?? '');
                  setEditing(true);
                }}
              >
                Edit
              </Button>
              <Button
                disabled={action.running || !mayWrite}
                title={mayWrite ? 'Send the current versions to the app again.' : whyDisabled('syncing the versions')}
                onClick={() =>
                  void action
                    .run(async () => {
                      const { versions: v } = await adminApi.syncAppVersions(applicationId);
                      return v.delivery.status === 'delivered'
                        ? `Synced. The app accepted revision ${v.revision}.`
                        : `Sent revision ${v.revision}. ${deliveryLine(v).text}`;
                    })
                    .then(() => onChanged())
                }
              >
                {action.running ? 'Syncing…' : 'Sync'}
              </Button>
            </span>
          </div>
        )}
        <div style={{ fontSize: '.84rem' }}>
          <span style={{ color: tone }}>{line.text}</span>
          {versions.delivery.deliveredAt ? (
            <span style={{ color: C.textMid }}>
              {' '}
              Last accepted <When iso={versions.delivery.deliveredAt} />.
            </span>
          ) : null}
          {versions.delivery.syncedAt ? (
            <span style={{ color: C.textMid }}>
              {' '}
              Last synced <When iso={versions.delivery.syncedAt} />.
            </span>
          ) : null}
        </div>
      </div>
    </PanelBody>
  );
}

/** Asks before a change that reaches everyone running the app. */
function ConfirmModal({
  title,
  question,
  confirmLabel,
  onClose,
  onConfirm,
}: {
  title: string;
  question: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [running, setRunning] = useState(false);
  return (
    <Modal
      title={title}
      onClose={onClose}
      width={480}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={running}
            onClick={() => {
              setRunning(true);
              void onConfirm();
            }}
          >
            {confirmLabel}
          </Button>
        </ModalActions>
      }
    >
      <p style={{ margin: 0, lineHeight: 1.5 }}>{question}</p>
    </Modal>
  );
}

const textareaStyle = { ...inputStyle, fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical' as const };

function ReleaseModal({
  note,
  suggestions,
  onClose,
  onSave,
}: {
  note: ReleaseNote | null;
  suggestions: { patch: string; minor: string; major: string } | null;
  onClose: () => void;
  onSave: (form: ReleaseForm) => Promise<void>;
}) {
  const saved = note ? formFromNote(note) : EMPTY_FORM;
  const [form, setForm] = useState<ReleaseForm>(saved);
  const set = (patch: Partial<ReleaseForm>) => setForm((f) => ({ ...f, ...patch }));
  const action = useAction();
  const problems = formProblems(form);
  const preview = previewBlocks(form.notes);

  return (
    <Modal
      title={note ? `Edit ${note.version}` : 'New release note'}
      subtitle={
        note?.published
          ? 'Published. Changes show in the app within a few minutes.'
          : 'Saved as a draft. Publish it from the list when the version is out.'
      }
      onClose={onClose}
      width={640}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || problems.length > 0 || (note !== null && !isDirty(form, saved))}
            onClick={() =>
              void action.run(async () => {
                await onSave(form);
                return 'Saved.';
              })
            }
          >
            {action.running ? 'Saving…' : note ? 'Save' : 'Save draft'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <div style={{ display: 'grid', gap: '.8rem' }}>
        <Field
          label="Version"
          error={form.version && problems[0]?.startsWith('The version') ? problems[0] : undefined}
          hint="MAJOR.MINOR.PATCH. Patch for fixes only, minor for something new people can use, major when the app changes as a whole."
        >
          <input
            value={form.version}
            onChange={(e) => set({ version: e.target.value })}
            placeholder="1.4.2"
            spellCheck={false}
            autoFocus={!note}
            style={inputStyle}
          />
        </Field>
        {suggestions ? (
          <div style={{ display: 'flex', gap: '.35rem', alignItems: 'center', fontSize: '.82rem', color: C.textMid }}>
            Next:
            <Button onClick={() => set({ version: suggestions.patch })}>Patch {suggestions.patch}</Button>
            <Button onClick={() => set({ version: suggestions.minor })}>Minor {suggestions.minor}</Button>
            <Button onClick={() => set({ version: suggestions.major })}>Major {suggestions.major}</Button>
          </div>
        ) : null}
        <Field label="Released on" hint="The day this version reached people. Left empty, publishing uses today.">
          <input
            type="date"
            value={form.releasedOn ?? ''}
            onChange={(e) => set({ releasedOn: e.target.value || null })}
            style={inputStyle}
          />
        </Field>
        <Field
          label="What changed"
          hint={`For the people using the app. A lead line, then a line starting with "- " for each change. No em dashes. ${form.notes.length.toLocaleString('en-US')} of ${NOTES_LIMIT.toLocaleString('en-US')} characters.`}
        >
          <textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={7} style={textareaStyle} />
        </Field>
        {preview.length > 0 ? (
          <div
            aria-label="Preview"
            style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '.7rem .9rem', fontSize: '.88rem' }}
          >
            <div style={{ fontWeight: 600, color: C.textMid, marginBottom: '.35rem' }}>What&rsquo;s new, as the app shows it</div>
            {preview.map((b, i) =>
              b.kind === 'bullet' ? (
                <div key={i} style={{ display: 'flex', gap: '.5rem', paddingLeft: '.3rem', margin: '.15rem 0' }}>
                  <span aria-hidden>•</span>
                  <span>{b.text}</span>
                </div>
              ) : (
                <p key={i} style={{ margin: '.2rem 0 .4rem' }}>
                  {b.text}
                </p>
              ),
            )}
          </div>
        ) : null}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '.45rem', fontSize: '.86rem' }}>
          <input
            type="checkbox"
            checked={form.significant}
            onChange={(e) => set({ significant: e.target.checked })}
            style={{ marginTop: '.2rem' }}
          />
          <span>
            <strong>Significant change</strong>
            <br />
            <span style={{ color: C.textMid }}>
              It changes what data is collected, the age rating, how the app makes money, or a major feature. The
              stores must be told before release and guardians asked again (Texas SB 2420).
            </span>
          </span>
        </label>
        {form.significant ? (
          <Field
            label="Note for parents"
            hint="Used for the store notice and the guardian message. Never shown in the app. Plain words, no em dashes."
          >
            <textarea
              value={form.significantNote}
              onChange={(e) => set({ significantNote: e.target.value })}
              rows={4}
              style={textareaStyle}
            />
          </Field>
        ) : null}
        {problems.length > 0 && !(problems.length === 1 && !form.version) ? (
          <div style={{ fontSize: '.84rem', color: C.dangerText }}>{problems.join(' ')}</div>
        ) : null}
      </div>
    </Modal>
  );
}
